/**
 * Small, dependency-free forecasting toolkit for the Forecast dashboard.
 *
 * Models are intentionally classical and explainable — an operator reading
 * "linear trend" or "Holt-Winters" on a card should be able to reason about
 * why the line goes where it goes. Each model returns point forecasts plus a
 * residual-based confidence band that widens with the horizon.
 *
 *   linear      ordinary least squares on the period index
 *   holt        double exponential smoothing (level + trend), grid-tuned
 *   seasonal    additive Holt-Winters; needs ≥ 2 full seasons of history
 *   moving_avg  flat forecast at the mean of the last k periods
 *   auto        backtests every applicable model on a holdout and keeps the
 *               one with the lowest absolute error, then refits on all data
 */

import type { ForecastMethod, ForecastPoint, SeriesPoint, TimeGrain } from './types';
import { stepBucket } from './series';

export interface ForecastResult {
  method: Exclude<ForecastMethod, 'auto'>;
  points: ForecastPoint[];
  /** In-sample one-step fit, aligned to `history` (null where undefined). */
  fitted: Array<number | null>;
  /** Mean absolute percentage error of the holdout backtest (auto) or in-sample fit. */
  mape: number | null;
  /** Residual standard deviation used for the band. */
  sigma: number;
}

/** z for an ~80% two-sided interval. Wide enough to be honest, tight enough to read. */
const BAND_Z = 1.28;

export function seasonPeriod(grain: TimeGrain): number {
  if (grain === 'month') return 12;
  if (grain === 'week') return 52;
  return 7;
}

function mean(xs: number[]): number {
  return xs.length === 0 ? 0 : xs.reduce((s, v) => s + v, 0) / xs.length;
}

function stdev(xs: number[]): number {
  if (xs.length < 2) return 0;
  const m = mean(xs);
  return Math.sqrt(xs.reduce((s, v) => s + (v - m) ** 2, 0) / (xs.length - 1));
}

function mapeOf(actual: number[], predicted: number[]): number | null {
  let total = 0;
  let n = 0;
  for (let i = 0; i < actual.length; i++) {
    if (!actual[i]) continue;
    total += Math.abs((actual[i] - predicted[i]) / actual[i]);
    n += 1;
  }
  return n === 0 ? null : total / n;
}

function maeOf(actual: number[], predicted: number[]): number {
  let total = 0;
  for (let i = 0; i < actual.length; i++) total += Math.abs(actual[i] - predicted[i]);
  return actual.length === 0 ? Number.POSITIVE_INFINITY : total / actual.length;
}

interface Fit {
  forecast: (h: number) => number[];
  fitted: Array<number | null>;
  residuals: number[];
}

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

function fitLinear(y: number[]): Fit {
  const n = y.length;
  if (n === 0) return { forecast: (h) => Array(h).fill(0), fitted: [], residuals: [] };
  if (n === 1) return { forecast: (h) => Array(h).fill(y[0]), fitted: [y[0]], residuals: [0] };
  const xs = y.map((_, i) => i);
  const mx = mean(xs);
  const my = mean(y);
  let sxy = 0;
  let sxx = 0;
  for (let i = 0; i < n; i++) {
    sxy += (xs[i] - mx) * (y[i] - my);
    sxx += (xs[i] - mx) ** 2;
  }
  const slope = sxx === 0 ? 0 : sxy / sxx;
  const intercept = my - slope * mx;
  const fitted = xs.map((x) => intercept + slope * x);
  return {
    forecast: (h) => Array.from({ length: h }, (_, k) => intercept + slope * (n + k)),
    fitted,
    residuals: y.map((v, i) => v - fitted[i]),
  };
}

function holtOnce(y: number[], alpha: number, beta: number): Fit {
  const n = y.length;
  if (n === 0) return { forecast: (h) => Array(h).fill(0), fitted: [], residuals: [] };
  let level = y[0];
  let trend = n > 1 ? y[1] - y[0] : 0;
  const fitted: Array<number | null> = [null];
  const residuals: number[] = [];
  for (let i = 1; i < n; i++) {
    const pred = level + trend;
    fitted.push(pred);
    residuals.push(y[i] - pred);
    const prevLevel = level;
    level = alpha * y[i] + (1 - alpha) * (level + trend);
    trend = beta * (level - prevLevel) + (1 - beta) * trend;
  }
  return {
    forecast: (h) => Array.from({ length: h }, (_, k) => level + trend * (k + 1)),
    fitted,
    residuals,
  };
}

function fitHolt(y: number[]): Fit {
  if (y.length < 3) return fitLinear(y);
  let best: Fit | null = null;
  let bestSse = Number.POSITIVE_INFINITY;
  for (const alpha of [0.2, 0.35, 0.5, 0.65, 0.8, 0.9]) {
    for (const beta of [0.05, 0.1, 0.2, 0.35, 0.5]) {
      const fit = holtOnce(y, alpha, beta);
      const sse = fit.residuals.reduce((s, r) => s + r * r, 0);
      if (sse < bestSse) {
        bestSse = sse;
        best = fit;
      }
    }
  }
  return best ?? fitLinear(y);
}

function holtWintersOnce(y: number[], m: number, alpha: number, beta: number, gamma: number): Fit {
  const n = y.length;
  const season1 = y.slice(0, m);
  const season2 = y.slice(m, 2 * m);
  let level = mean(season1);
  let trend = (mean(season2) - mean(season1)) / m;
  const seasonal = season1.map((v) => v - level);
  const fitted: Array<number | null> = [];
  const residuals: number[] = [];
  for (let i = 0; i < n; i++) {
    const s = seasonal[i % m];
    const pred = level + trend + s;
    if (i < m) {
      fitted.push(null);
    } else {
      fitted.push(pred);
      residuals.push(y[i] - pred);
    }
    const prevLevel = level;
    level = alpha * (y[i] - s) + (1 - alpha) * (level + trend);
    trend = beta * (level - prevLevel) + (1 - beta) * trend;
    seasonal[i % m] = gamma * (y[i] - level) + (1 - gamma) * s;
  }
  return {
    forecast: (h) =>
      Array.from({ length: h }, (_, k) => level + trend * (k + 1) + seasonal[(n + k) % m]),
    fitted,
    residuals,
  };
}

function fitSeasonal(y: number[], m: number): Fit | null {
  if (y.length < 2 * m) return null;
  let best: Fit | null = null;
  let bestSse = Number.POSITIVE_INFINITY;
  for (const alpha of [0.2, 0.4, 0.6, 0.8]) {
    for (const beta of [0.05, 0.15, 0.3]) {
      for (const gamma of [0.1, 0.3, 0.5]) {
        const fit = holtWintersOnce(y, m, alpha, beta, gamma);
        const sse = fit.residuals.reduce((s, r) => s + r * r, 0);
        if (sse < bestSse) {
          bestSse = sse;
          best = fit;
        }
      }
    }
  }
  return best;
}

function fitMovingAverage(y: number[], k: number): Fit {
  const n = y.length;
  const window = Math.max(1, Math.min(k, n));
  const fitted: Array<number | null> = [];
  const residuals: number[] = [];
  for (let i = 0; i < n; i++) {
    if (i < window) {
      fitted.push(null);
      continue;
    }
    const pred = mean(y.slice(i - window, i));
    fitted.push(pred);
    residuals.push(y[i] - pred);
  }
  const level = mean(y.slice(n - window));
  return { forecast: (h) => Array(h).fill(level), fitted, residuals };
}

function fitBy(method: Exclude<ForecastMethod, 'auto'>, y: number[], period: number, maWindow: number): Fit | null {
  switch (method) {
    case 'linear':
      return fitLinear(y);
    case 'holt':
      return fitHolt(y);
    case 'seasonal':
      return fitSeasonal(y, period);
    case 'moving_avg':
      return fitMovingAverage(y, maWindow);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface ForecastOptions {
  method: ForecastMethod;
  horizon: number;
  grain: TimeGrain;
  /** Clamp the lower band (and point forecast) at zero — true for counts and revenue. */
  nonNegative?: boolean;
}

/**
 * `history` must be a contiguous, gap-filled, ascending series of complete
 * periods. The forecast starts at the period after the last history point.
 */
export function forecastSeries(history: SeriesPoint[], opts: ForecastOptions): ForecastResult {
  const y = history.map((p) => p.v);
  const n = y.length;
  const period = seasonPeriod(opts.grain);
  const maWindow = opts.grain === 'day' ? 7 : 3;
  const nonNegative = opts.nonNegative ?? true;
  const horizon = Math.max(1, Math.floor(opts.horizon));
  const lastT = history[n - 1]?.t;

  const emit = (method: Exclude<ForecastMethod, 'auto'>, fit: Fit, mape: number | null): ForecastResult => {
    const sigma = stdev(fit.residuals);
    const raw = fit.forecast(horizon);
    const points: ForecastPoint[] = raw.map((v, k) => {
      const spread = BAND_Z * sigma * Math.sqrt(k + 1);
      const point = nonNegative ? Math.max(0, v) : v;
      const lo = nonNegative ? Math.max(0, v - spread) : v - spread;
      const hi = nonNegative ? Math.max(0, v + spread) : v + spread;
      return {
        t: lastT ? stepBucket(lastT, opts.grain, k + 1) : String(k),
        v: point,
        lo,
        hi,
      };
    });
    return { method, points, fitted: fit.fitted, mape, sigma };
  };

  if (n === 0) {
    return { method: 'moving_avg', points: [], fitted: [], mape: null, sigma: 0 };
  }

  if (opts.method !== 'auto') {
    const fit = fitBy(opts.method, y, period, maWindow) ?? fitHolt(y);
    const method = fit === null ? 'holt' : opts.method;
    const inSample = fit.fitted
      .map((f, i) => (f == null ? null : [y[i], f]))
      .filter((p): p is [number, number] => p !== null);
    return emit(
      method,
      fit,
      mapeOf(
        inSample.map((p) => p[0]),
        inSample.map((p) => p[1]),
      ),
    );
  }

  // Auto: backtest on a holdout of the most recent periods.
  if (n < 4) {
    const fit = fitMovingAverage(y, maWindow);
    return emit('moving_avg', fit, null);
  }
  const holdout = Math.max(1, Math.min(3, Math.floor(n / 4)));
  const train = y.slice(0, n - holdout);
  const test = y.slice(n - holdout);
  const candidates: Array<Exclude<ForecastMethod, 'auto'>> = ['moving_avg', 'linear', 'holt', 'seasonal'];
  let bestMethod: Exclude<ForecastMethod, 'auto'> = 'moving_avg';
  let bestErr = Number.POSITIVE_INFINITY;
  let bestMape: number | null = null;
  for (const method of candidates) {
    const fit = fitBy(method, train, period, maWindow);
    if (!fit) continue;
    const pred = fit.forecast(holdout);
    const err = maeOf(test, pred);
    if (err < bestErr) {
      bestErr = err;
      bestMethod = method;
      bestMape = mapeOf(test, pred);
    }
  }
  const finalFit = fitBy(bestMethod, y, period, maWindow) ?? fitLinear(y);
  return emit(bestMethod, finalFit, bestMape);
}

/** Compound growth per period implied by first/last of a series (0..1). */
export function impliedGrowth(points: SeriesPoint[]): number | null {
  if (points.length < 2) return null;
  const first = points[0].v;
  const last = points[points.length - 1].v;
  if (first <= 0 || last <= 0) return null;
  return Math.pow(last / first, 1 / (points.length - 1)) - 1;
}

/**
 * Walk a cash balance forward: `start` plus the cumulative projected net
 * per period. Bands accumulate the per-period uncertainty.
 */
export function projectBalance(start: number, net: ForecastPoint[]): ForecastPoint[] {
  let v = start;
  let lo = start;
  let hi = start;
  return net.map((p) => {
    v += p.v;
    lo += p.lo;
    hi += p.hi;
    return { t: p.t, v, lo: Math.min(lo, v), hi: Math.max(hi, v) };
  });
}

/** First forecast period whose point value drops to or below zero, if any. */
export function zeroCrossing(points: ForecastPoint[]): string | null {
  for (const p of points) if (p.v <= 0) return p.t;
  return null;
}
